import React, { useState, useEffect } from "react";
import "./Number.css";
import { fromMmToUnit, fromUnitToMm } from "src/helpers/dimensions";

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  label?: string;
  showSubLabel?: boolean;
  subStep?: number;
  unit?: "mm" | "cm" | "deg";
  Svg?: React.FC;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  label,
  showSubLabel = true,
  subStep,
  unit = "mm",
  Svg,
}) => {
  const [inputValue, setInputValue] = useState(() => fromMmToUnit(value, unit));
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const roundValue = (num: number, numStep: number) => {
    if (min !== undefined && num < min) {
      num = min;
      return min;
    } else if (max !== undefined && num > max) {
      num = max;
      return max;
    }

    num = Math.round(num / numStep) * numStep;

    return num;
  };

  const updateValue = (deltaInUnit: number, stepInMm: number) => {
    const currentValueInMm = fromUnitToMm(inputValue, unit);
    const deltaInMm = fromUnitToMm(deltaInUnit, unit);
    const newValueInMm = currentValueInMm + deltaInMm;
    const roundedMm = roundValue(newValueInMm, stepInMm);
    setInputValue(fromMmToUnit(roundedMm, unit));
    onChange(roundedMm);
  };

  const handleMinusClick = () => {
    updateValue(-1 * fromMmToUnit(step, unit), step);
  };

  const handlePlusClick = () => {
    updateValue(fromMmToUnit(step, unit), step);
  };

  const handleSubMinusClick = () => {
    const actualSubStep = subStep ?? step; // тоже в мм
    updateValue(-1 * fromMmToUnit(actualSubStep, unit), actualSubStep);
  };

  const handleSubPlusClick = () => {
    const actualSubStep = subStep ?? step;
    updateValue(fromMmToUnit(actualSubStep, unit), actualSubStep);
  };

  const handleInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10) || 0;
    setInputValue(val);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setTypingTimeout(
      setTimeout(() => {
        const valInMm = fromUnitToMm(val, unit);
        const actualSubStep = subStep ?? step;
        const roundedMm = roundValue(valInMm, actualSubStep);
        setInputValue(fromMmToUnit(roundedMm, unit));
        onChange(roundedMm);
      }, 600)
    );
  };

  useEffect(() => {
    setInputValue(fromMmToUnit(value, unit));
  }, [value, unit]);

  const minForInput = fromMmToUnit(min, unit);
  const maxForInput = fromMmToUnit(max, unit);
  const stepForInput = fromMmToUnit(step, unit);

  return (
    <div className="number-input">
      <div className="number-input__label">
        <div className="number-input__label_text">
          {Svg && (
            <div>
              {" "}
              <Svg />
            </div>
          )}
          {label && <span>{label}</span>}
        </div>
        {showSubLabel && (
          <span>
            (min {minForInput} - max {maxForInput})
          </span>
        )}
      </div>

      <div className="number-input__wrapper">
        {subStep && (
          <button
            className="number-input__minus--sub"
            onClick={handleSubMinusClick}
          >
            –
          </button>
        )}
        <button className="number-input__minus" onClick={handleMinusClick}>
          –
        </button>
        <div className="number-input__input">
          <input
            type="number"
            value={inputValue}
            onChange={handleInputValue}
            min={minForInput}
            max={maxForInput}
            step={stepForInput}
          />
          <span className="number-input__unit">{unit}</span>
        </div>

        <button className="number-input__plus" onClick={handlePlusClick}>
          +
        </button>

        {subStep && (
          <button
            className="number-input__plus--sub"
            onClick={handleSubPlusClick}
          >
            +
          </button>
        )}
      </div>
    </div>
  );
};

export default NumberInput;
